import React, { Fragment, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from 'react-apollo';
import { Formik } from 'formik';
import pMap from 'p-map-browser';

import { AlbumFolderForSelection } from '../../../graphql/queries/album-folder';
import { albumFolderUpdate as albumFolderUpdateMutation } from '../../../graphql/mutations/album-folder';
import Spinner from '../Spinner';
import Alert from '../Alert';
import Button from '../Button';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';
import useGlobalToast from '../../../hooks/useGlobalToast';
import { PHOTO_UPLOAD_TYPE } from '../../../constants/photo-types';
import CustomControl from '../CustomControl';
import { STEPS } from './constants';

const propTypes = {
  albumFolderId: PropTypes.string.isRequired,
  onRequestClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

const PricingCategoryHierarchyGreenScreenSettings = ({
  albumFolderId,
  onRequestClose,
  onSuccess,
}) => {
  const openAlert = useOpenAlert();
  const { setToast } = useGlobalToast();
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.INDIVIDUAL_PHOTOS);

  const { data: { albumFolder } = {}, loading, error } = useQuery(
    AlbumFolderForSelection,
    {
      variables: { id: albumFolderId },
      skip: !albumFolderId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const [albumFolderUpdate] = useMutation(albumFolderUpdateMutation);

  const subFolders = useMemo(() => {
    return albumFolder?.children?.album_folders?.records || [];
  }, [albumFolder]);

  const initialValues = useMemo(() => {
    const values = {};
    if (subFolders.length === 0) {
      values[`${albumFolder?.id}_individual`] =
        albumFolder?.individual_photos_upload_type;
      values[`${albumFolder?.id}_team`] = albumFolder?.team_photos_upload_type;
    }
    subFolders.forEach(folder => {
      values[`${folder.id}_individual`] = folder.individual_photos_upload_type;
      values[`${folder.id}_team`] = folder.team_photos_upload_type;
    });
    return values;
  }, [subFolders]);

  const validPhotoUploadTypes = useMemo(() => {
    if (currentStep === STEPS.INDIVIDUAL_PHOTOS) {
      return Object.values(PHOTO_UPLOAD_TYPE).filter(
        type => type !== PHOTO_UPLOAD_TYPE.GREEN_SCREEN_COMPOSITE,
      );
    }

    return Object.values(PHOTO_UPLOAD_TYPE);
  }, [currentStep]);

  if (loading) {
    return (
      <div className="text-center m-6 p-6">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center m-6 p-6">
        <Alert type="danger">
          Error loading album folder data: {getErrorString(error)}
        </Alert>
      </div>
    );
  }

  const isLastStep = currentStep === STEPS.TEAM_PHOTOS;

  const onSubmit = async values => {
    if (saving) {
      return;
    }

    if (!isLastStep) {
      setCurrentStep(STEPS.TEAM_PHOTOS);
      return;
    }

    setSaving(true);

    try {
      if (subFolders.length === 0) {
        const individualUploadType = values[`${albumFolder.id}_individual`];
        const teamUploadType = values[`${albumFolder.id}_team`];

        const greenScreenEnabled = (() => {
          if (
            individualUploadType === PHOTO_UPLOAD_TYPE.GREEN_SCREEN_ENABLED ||
            teamUploadType === PHOTO_UPLOAD_TYPE.GREEN_SCREEN_ENABLED
          ) {
            return true;
          }

          if (
            individualUploadType === PHOTO_UPLOAD_TYPE.GREEN_SCREEN_DISABLED &&
            teamUploadType === PHOTO_UPLOAD_TYPE.GREEN_SCREEN_DISABLED
          ) {
            return false;
          }

          // Letting this be undefined will let it keep root value.
          return undefined;
        })();

        await albumFolderUpdate({
          variables: {
            input: {
              album_folder_id: albumFolder.id,
              green_screen_enabled: greenScreenEnabled,
              individual_photos_upload_type: individualUploadType,
              team_photos_upload_type: teamUploadType,
            },
          },
        });
      }
      await pMap(subFolders, async folder => {
        const individualUploadType = values[`${folder.id}_individual`];
        const teamUploadType = values[`${folder.id}_team`];

        const greenScreenEnabled = (() => {
          if (
            individualUploadType === PHOTO_UPLOAD_TYPE.GREEN_SCREEN_ENABLED ||
            teamUploadType === PHOTO_UPLOAD_TYPE.GREEN_SCREEN_ENABLED
          ) {
            return true;
          }

          if (
            individualUploadType === PHOTO_UPLOAD_TYPE.GREEN_SCREEN_DISABLED &&
            teamUploadType === PHOTO_UPLOAD_TYPE.GREEN_SCREEN_DISABLED
          ) {
            return false;
          }

          // Letting this be undefined will let it keep root value.
          return undefined;
        })();

        await albumFolderUpdate({
          variables: {
            input: {
              album_folder_id: folder.id,
              individual_photos_upload_type: individualUploadType,
              team_photos_upload_type: teamUploadType,
              green_screen_enabled: greenScreenEnabled,
            },
          },
        });
      });

      setToast('Your settings have been updated successfully!');
      onSuccess?.();
      onRequestClose?.();
    } catch (e) {
      openAlert({
        message: 'Error updating settings.',
        info: getErrorString(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const title = (() => {
    if (currentStep === STEPS.INDIVIDUAL_PHOTOS) {
      return (
        <Fragment>
          Please confirm how your{' '}
          <span className="text-primary">Individual photos</span> are being shot
          for each category
        </Fragment>
      );
    }

    return (
      <Fragment>
        Please confirm how your{' '}
        <span className="text-primary">
          {albumFolder?.name === 'School Portraits' ? 'Group' : 'Team'} photos
        </span>{' '}
        are being shot for each category
      </Fragment>
    );
  })();

  return (
    <Fragment>
      <div className="px-4 py-3">
        <h4 className="text-center mb-4 fw-bold text-dark">{title}</h4>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validateOnMount={false}
          render={({ values, handleChange, handleSubmit }) => {
            const fieldSuffix =
              currentStep === STEPS.INDIVIDUAL_PHOTOS ? '_individual' : '_team';

            const isFormComplete = (() => {
              if (!subFolders.length) {
                return !!values?.[`${albumFolder.id}${fieldSuffix}`];
              }

              if (currentStep === STEPS.INDIVIDUAL_PHOTOS) {
                return subFolders.every(folder =>
                  Object.values(PHOTO_UPLOAD_TYPE).includes(
                    values?.[`${folder.id}_individual`],
                  ),
                );
              }

              return subFolders.every(folder =>
                Object.values(PHOTO_UPLOAD_TYPE).includes(
                  values?.[`${folder.id}_team`],
                ),
              );
            })();

            const onSelectAll = uploadType => {
              subFolders.forEach(folder => {
                const fieldName = `${folder.id}${fieldSuffix}`;
                handleChange({
                  target: {
                    name: fieldName,
                    value: uploadType,
                  },
                });
              });
            };

            return (
              <form onSubmit={handleSubmit}>
                <div className="table-responsive mb-4">
                  <table className="table table-striped table-hover border">
                    <thead className="table-light">
                      <tr>
                        <th className="text-bold v-align-middle-i">
                          Categories
                        </th>
                        {currentStep !== STEPS.INDIVIDUAL_PHOTOS && (
                          <th className="text-center text-bold">
                            Upload as knocked out
                            <br />
                            composites
                            {subFolders.length > 0 && (
                              <Fragment>
                                <br />
                                <Button
                                  btnSize="sm"
                                  btnStyle="link"
                                  onClick={() =>
                                    onSelectAll(
                                      PHOTO_UPLOAD_TYPE.GREEN_SCREEN_COMPOSITE,
                                    )
                                  }
                                >
                                  Select for all below
                                </Button>
                              </Fragment>
                            )}
                          </th>
                        )}
                        <th className="text-center text-bold">
                          Require knockout by
                          <br />
                          Waldo
                          {subFolders.length > 0 && (
                            <Fragment>
                              <br />
                              <Button
                                btnSize="sm"
                                btnStyle="link"
                                onClick={() =>
                                  onSelectAll(
                                    PHOTO_UPLOAD_TYPE.GREEN_SCREEN_ENABLED,
                                  )
                                }
                              >
                                Select for all below
                              </Button>
                            </Fragment>
                          )}
                        </th>
                        <th className="text-center text-bold">
                          No knockout — uploading
                          <br />
                          with natural backgrounds
                          {subFolders.length > 0 && (
                            <Fragment>
                              <br />
                              <Button
                                btnSize="sm"
                                btnStyle="link"
                                onClick={() =>
                                  onSelectAll(
                                    PHOTO_UPLOAD_TYPE.GREEN_SCREEN_DISABLED,
                                  )
                                }
                              >
                                Select for all below
                              </Button>
                            </Fragment>
                          )}
                        </th>
                        <th className="text-center text-bold">
                          Not shooting in this
                          <br />
                          gallery
                          {subFolders.length > 0 && (
                            <Fragment>
                              <br />
                              <Button
                                btnSize="sm"
                                btnStyle="link"
                                onClick={() =>
                                  onSelectAll(PHOTO_UPLOAD_TYPE.NOT_UPLOADING)
                                }
                              >
                                Select for all below
                              </Button>
                            </Fragment>
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subFolders.length === 0 && (
                        <tr key={albumFolder.id} className="align-middle">
                          <td className="fw-medium py-3 px-4">
                            {albumFolder.name}
                          </td>
                          {validPhotoUploadTypes.map(uploadType => {
                            const fieldName = `${albumFolder.id}${fieldSuffix}`;
                            const radioId = `${albumFolder.id}_${uploadType}${fieldSuffix}`;

                            return (
                              <td key={uploadType} className="text-center py-3">
                                <CustomControl
                                  type="radio"
                                  id={radioId}
                                  name={fieldName}
                                  value={uploadType}
                                  label={radioId}
                                  labelSrOnly
                                  checked={values[fieldName] === uploadType}
                                  onChange={handleChange}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      )}
                      {subFolders.map(folder => {
                        const fieldName = `${folder.id}${fieldSuffix}`;
                        const currentValue = values[fieldName];

                        return (
                          <tr key={folder.id} className="align-middle">
                            <td className="fw-medium py-3 px-4">
                              {folder.name}
                            </td>
                            {validPhotoUploadTypes.map(uploadType => {
                              const radioId = `${folder.id}_${uploadType}${fieldSuffix}`;

                              return (
                                <td
                                  key={uploadType}
                                  className="text-center py-3"
                                >
                                  <CustomControl
                                    type="radio"
                                    id={radioId}
                                    name={fieldName}
                                    value={uploadType}
                                    label={radioId}
                                    labelSrOnly
                                    checked={currentValue === uploadType}
                                    onChange={handleChange}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="text-center mt-4">
                  <div className="d-flex align-items-center justify-content-center">
                    <Button
                      type="button"
                      btnStyle="link"
                      className="mr-6"
                      onClick={() => {
                        if (currentStep === STEPS.TEAM_PHOTOS) {
                          setCurrentStep(STEPS.INDIVIDUAL_PHOTOS);
                        } else {
                          onRequestClose?.();
                        }
                      }}
                      disabled={saving}
                    >
                      &larr; Go Back
                    </Button>
                    <Button
                      type="submit"
                      btnStyle="primary"
                      disabled={saving || !isFormComplete}
                    >
                      {(() => {
                        if (!isLastStep) {
                          return 'Continue';
                        }
                        return saving ? 'Saving...' : 'Confirm';
                      })()}
                    </Button>
                  </div>
                </div>
              </form>
            );
          }}
        />
      </div>
    </Fragment>
  );
};

PricingCategoryHierarchyGreenScreenSettings.propTypes = propTypes;

export default PricingCategoryHierarchyGreenScreenSettings;
